import {FunctionComponent, useEffect, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {route} from 'b302-frontend-library';

import { ReactComponent as Image } from '../../../../assets/images/illustratie_compare.svg';
import BarChart from '../../partials/compare/BarChart';
import HanButton from '../../../utils/HanButton';

import {CompareResponse} from '../../../../networking/repos/CompareRepository';
import University from '../../../../networking/models/University';
import CompareTitle from '../../partials/compare/CompareTitle';
import Breadcrumbs from '../../../utils/Breadcrumbs';
import {error} from '../../../../utils/Toast';
import { compareColors } from '../../../../config/theming';

export const useCompareParams = (): [number[], string] => {
    const navigate = useNavigate();
    const params = useParams();
    const ids = params.ids!;

    const idArray = useMemo(() => {
        return ids.split(',').map(id => Number(id)).sort();
    }, [ids]);

    useEffect(() => {
        if(idArray.length > 1)
            return;

        navigate(route('universities'));
        error('Select at least two universities');
    }, [idArray]);

    return [idArray, ids];
};

interface Props {
    universities: University[];
    compareResponse: CompareResponse;
}

const CompareContent: FunctionComponent<Props> = (props) => {
    const [, idString] = useCompareParams();
    const navigate = useNavigate();

    const routes = [
        route('compare-facilities', { ids: idString }),
        route('compare-accommodation', { ids: idString }),
        route('compare-costs', { ids: idString }),
        route('compare-program', { ids: idString }),
    ];

    return (
        <>
            <Image className={'compare-home-page__image'} />

            <div className={'compare-home-page__content'}>
                <CompareTitle colors={compareColors} universities={props.universities} />

                <Breadcrumbs
                    breadcrumbs={
                        [
                            { name: "Home", url: "/" },
                            { name: "Universities", url: "/universities" },
                            { name: "Compare", url: `/compare/${idString}` }
                        ]
                    }
                />

                <div className={'compare-home-page__sections'}>
                    {props.compareResponse.sections.map((section, index) => (
                        <div className={'compare-home-page__section'} key={section.title}>
                            <span className={'compare-home-page__text compare-home-page__text--title'}>{section.title}</span>

                            <div className={'compare-home-page__chart-section'}>
                                <BarChart colors={compareColors} values={section.results} extras={section.extras} />
                                <HanButton onClick={() => navigate(routes[index])} arrow />
                            </div>
                        </div>
                    ))}
                </div>

                {props.universities.length > 0 &&
                    <div className={'compare-home-page__reviews'}>
                        <span className={'compare-home-page__text compare-home-page__text--title'}>Reviews of</span>

                        <div className={'compare-home-page__reviews-container'}>
                            {props.universities.map((university, index) => (
                                <div className={'compare-home-page__review'} key={university.id}>
                                    <span className={'compare-home-page__text compare-home-page__text--sub-title'}>Review {university.name}</span>
                                    <span className={'compare-home-page__text'}>{props.compareResponse.reviews[index]}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                }
            </div>
        </>
    );
};

export default CompareContent;
